import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { CalendarPlus, Search } from "lucide-react";
import { api } from "@jourfx/convex/client";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@jourfx/ui";

import { PageHeader } from "../../components/PageHeader";
import { useSession } from "../../lib/auth";
import { useDateFormat } from "../../hooks/useDateFormat";
import { APPOINTMENT_STATUSES, STATUS_VARIANT, type AppointmentStatus } from "./shared";

type AppointmentRow = {
  _id: string;
  organizationId: string;
  organizationName?: string | null;
  versicherterName?: string | null;
  gutachterName?: string | null;
  templateName?: string | null;
  scheduledAt: number;
  duration: number;
  status: AppointmentStatus;
  hasCredentials?: boolean;
};

type OrganizationOption = {
  _id: string;
  name: string;
};

function AppointmentStatusBadge({ status }: { status: AppointmentStatus }) {
  const { t } = useTranslation("appointments");
  return (
    <Badge variant={STATUS_VARIANT[status] ?? "outline"}>
      {t(`status.${status}`)}
    </Badge>
  );
}

export function AppointmentsList() {
  const apiRef = api as any;
  const { t } = useTranslation("appointments");
  const { formatDateTime } = useDateFormat();
  const session = useSession();
  const canSelectOrganization = session.isAdministrator || session.isSuperAdmin;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<AppointmentStatus | "all">("all");
  const [organizationId, setOrganizationId] = useState<string>("");

  const organizations = useQuery(
    apiRef.admin.organizations.list,
    canSelectOrganization ? { status: "active", limit: 100 } : "skip"
  ) as { organizations: OrganizationOption[] } | undefined;

  const selectedOrgId = organizationId || undefined;

  const appointments = useQuery(apiRef.admin.appointments.list, {
    organizationId: selectedOrgId as any,
    status: status === "all" ? undefined : status,
    search: search.trim() || undefined,
  }) as AppointmentRow[] | undefined;

  const rows = useMemo(() => {
    if (!appointments) return [];
    return [...appointments].sort((left, right) => left.scheduledAt - right.scheduledAt);
  }, [appointments]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        actions={
          <Link
            to="/appointments/new"
            search={
              selectedOrgId
                ? ({ organizationId: selectedOrgId } as any)
                : undefined
            }
          >
            <Button>
              <CalendarPlus className="mr-2 h-4 w-4" />
              {t("list.create")}
            </Button>
          </Link>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("list.filters")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          {canSelectOrganization ? (
            <div className="space-y-2">
              <Label htmlFor="appointments-organization-filter">
                {t("list.organization")}
              </Label>
              <select
                id="appointments-organization-filter"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={organizationId}
                onChange={(event) => setOrganizationId(event.target.value)}
              >
                <option value="">{t("list.allOrganizations")}</option>
                {organizations?.organizations?.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="appointments-status-filter">{t("list.status")}</Label>
            <select
              id="appointments-status-filter"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as AppointmentStatus | "all")
              }
            >
              <option value="all">{t("list.allStatuses")}</option>
              {APPOINTMENT_STATUSES.map((statusValue) => (
                <option key={statusValue} value={statusValue}>
                  {t(`status.${statusValue}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="appointments-search">{t("list.searchLabel")}</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="appointments-search"
                className="pl-9"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("list.searchPlaceholder")}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("list.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  {canSelectOrganization ? (
                    <th className="px-3 py-2 font-medium">{t("list.columns.organization")}</th>
                  ) : null}
                  <th className="px-3 py-2 font-medium">{t("list.columns.patient")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.examiner")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.template")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.scheduledAt")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.duration")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.status")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.credentials")}</th>
                  <th className="px-3 py-2 font-medium text-right">
                    {t("list.columns.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows?.map((appointment) => (
                  <tr key={appointment._id} className="border-t">
                    {canSelectOrganization ? (
                      <td className="px-3 py-2">{appointment.organizationName || "-"}</td>
                    ) : null}
                    <td className="px-3 py-2">{appointment.versicherterName || "-"}</td>
                    <td className="px-3 py-2">{appointment.gutachterName || "-"}</td>
                    <td className="px-3 py-2">{appointment.templateName || "-"}</td>
                    <td className="px-3 py-2">{formatDateTime(appointment.scheduledAt)}</td>
                    <td className="px-3 py-2">
                      {t("list.minutes", { count: appointment.duration })}
                    </td>
                    <td className="px-3 py-2">
                      <AppointmentStatusBadge status={appointment.status} />
                    </td>
                    <td className="px-3 py-2">
                      {appointment.hasCredentials
                        ? t("list.credentialsReady")
                        : t("list.credentialsMissing")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to="/appointments/$appointmentId"
                        params={{ appointmentId: appointment._id as any }}
                        className="text-primary hover:underline"
                      >
                        {t("list.view")}
                      </Link>
                    </td>
                  </tr>
                ))}
                {rows?.length === 0 ? (
                  <tr>
                    <td
                      className="px-3 py-8 text-center text-muted-foreground"
                      colSpan={canSelectOrganization ? 9 : 8}
                    >
                      {t("list.empty")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
